// The blue/white design tokens and form primitives the store screens share.
//
// Pulled out of StoreMaster so the Branch section can look like the rest of the
// screen without a second, slowly-diverging copy of the same twelve constants.

import {
  Box,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

/* ─── Blue/White design tokens (shared with the rest of the app) ─── */
export const BLUE = "#2563EB";
export const BLUE_DARK = "#1D4ED8";
export const BLUE_PALE = "#EFF6FF";
export const INK = "#0F172A";
export const INK_SOFT = "#334155";
export const INK_MUTED = "#64748B";
export const BORDER = "#E2E8F0";
export const SURFACE = "#FFFFFF";
export const BG_ALT = "#F8FAFC";
export const SUCCESS = "#059669";
export const DANGER = "#DC2626";

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: SURFACE,
    borderRadius: 2,
    fontSize: "0.85rem",
    color: INK,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BLUE },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "1.5px" },
    // The flat borderColor rules above would otherwise swallow the error state
    "&.Mui-error fieldset, &.Mui-error:hover fieldset, &.Mui-error.Mui-focused fieldset":
      { borderColor: DANGER },
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.66rem",
    ml: 0.5,
    mt: 0.3,
    minHeight: 14,
  },
  "& .MuiInputBase-multiline": {
    alignItems: "flex-start",
    "& .MuiInputAdornment-positionStart": { mt: "7px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: INK_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
  "& .MuiInputLabel-asterisk": { color: DANGER },
  "& input::placeholder, & textarea::placeholder": {
    color: INK_MUTED,
    opacity: 1,
  },
};

export const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: BLUE },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: BLUE,
  },
};

export const popperSx = {
  "& .MuiAutocomplete-paper": {
    borderRadius: 2,
    border: `1px solid ${BORDER}`,
    boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
  },
  "& .MuiAutocomplete-option": { fontSize: "0.83rem", color: INK_SOFT },
  "& .MuiAutocomplete-option[aria-selected='true']": { bgcolor: BLUE_PALE },
  "& .MuiAutocomplete-noOptions": { fontSize: "0.8rem", color: INK_MUTED },
};

export const FieldIcon = ({ children }) => (
  <InputAdornment position="start">
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        "& svg": { fontSize: 18, color: INK_MUTED, fill: INK_MUTED },
      }}
    >
      {children}
    </Box>
  </InputAdornment>
);

// Outlined field with a leading icon. The label is kept shrunk because MUI
// floats it over a start adornment otherwise.
export const IconField = ({ icon, sx, ...rest }) => (
  <TextField
    size="small"
    fullWidth
    InputProps={
      icon ? { startAdornment: <FieldIcon>{icon}</FieldIcon> } : undefined
    }
    InputLabelProps={{ shrink: true }}
    {...rest}
    sx={{ ...fieldSx, ...sx }}
  />
);

// Card with an icon tile + title header, used for every form section.
export const SectionCard = ({ icon, title, caption, action, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 3,
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        px: { xs: 1.75, sm: 2.25 },
        py: 1.25,
        borderBottom: `1px solid ${BORDER}`,
        bgcolor: BG_ALT,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 2,
            bgcolor: BLUE_PALE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 17, color: BLUE_DARK, fill: BLUE_DARK },
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: INK }}>
            {title}
          </Typography>
          {caption && (
            <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>

    <Box sx={{ p: { xs: 1.75, sm: 2.25 }, flex: 1 }}>{children}</Box>
  </Box>
);

// Labelled block inside a card. `divider` draws a hairline above it so stacked
// sub-sections stay visually separate without nesting more cards.
export const SubSection = ({ label, hint, divider, children }) => (
  <Box
    sx={{
      pt: divider ? 1.75 : 0,
      borderTop: divider ? `1px dashed ${BORDER}` : "none",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 0.75,
        flexWrap: "wrap",
        mb: 1.25,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          color: BLUE_DARK,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: "0.66rem", color: INK_MUTED }}>
          · {hint}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

// 12-column responsive grid — all form rows share it.
export const FieldGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(12, 1fr)",
      columnGap: { xs: 1.25, sm: 2 },
      rowGap: { xs: 1.75, sm: 2 },
    }}
  >
    {children}
  </Box>
);

// `lg` is the two-column-dashboard width: from lg up each card is only half the
// page wide, so most fields go back to spanning more columns inside the card.
export const Cell = ({ span, children }) => {
  const xs = span.xs ?? 12;
  const sm = span.sm ?? xs;
  const md = span.md ?? sm;
  const lg = span.lg ?? md;

  return (
    <Box
      sx={{
        gridColumn: {
          xs: `span ${xs}`,
          sm: `span ${sm}`,
          md: `span ${md}`,
          lg: `span ${lg}`,
        },
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
};

// Segmented pill selector — replaces the bare radio rows.
export const Segmented = ({ options, value, onChange, size = "md" }) => (
  <Box
    sx={{
      display: "inline-flex",
      p: 0.4,
      gap: 0.4,
      borderRadius: 999,
      bgcolor: BG_ALT,
      border: `1px solid ${BORDER}`,
      maxWidth: "100%",
      overflow: "hidden",
    }}
  >
    {options.map((option) => {
      const active = value === option.value;
      const accent = option.color || BLUE;
      return (
        <Box
          key={option.value}
          onClick={() => onChange(option.value)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: size === "sm" ? 1.25 : 1.75,
            py: size === "sm" ? 0.35 : 0.6,
            borderRadius: 999,
            cursor: "pointer",
            userSelect: "none",
            transition: "all 0.18s ease",
            bgcolor: active ? accent : "transparent",
            color: active ? "#fff" : INK_MUTED,
            fontWeight: active ? 700 : 500,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: active ? accent : "#E9EEF5" },
          }}
        >
          {option.dot && (
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: active ? "#fff" : accent,
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: size === "sm" ? "0.7rem" : "0.76rem",
              fontWeight: "inherit",
            }}
          >
            {option.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

// Switch presented as a labelled row so the intent of the toggle is obvious.
export const ToggleRow = ({ title, caption, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: `1px solid ${checked ? "#BFDBFE" : BORDER}`,
      bgcolor: checked ? BLUE_PALE : BG_ALT,
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.2s ease",
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: checked ? BLUE_DARK : INK_SOFT,
        }}
      >
        {title}
      </Typography>
      {caption && (
        <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
          {caption}
        </Typography>
      )}
    </Box>
    <Switch
      size="small"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      sx={switchSx}
    />
  </Box>
);
